import { useState } from 'react'

import { TableSortLabel } from '@mui/material'

import dayjs from 'dayjs'

import { FieldSelectComponent } from '../FieldSelect'

import './index.css'

const formatDate = (dateStr) => dayjs(dateStr).format('MMM D, YYYY [at] h:mm A')

const sortColumns = [
    { value: 'name', label: 'Deal' },
    { value: 'price', label: 'Price' },
    { value: 'status', label: 'Status' },
    { value: 'clicks', label: 'Clicks' },
    { value: 'clicksDead', label: 'Dead' },
    { value: 'date', label: 'Date' }
]

const RedirectIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="7" y1="17" x2="17" y2="7"></line>
        <polyline points="7 7 17 7 17 17"></polyline>
    </svg>
)

const SortDirectionIcon = ({ direction }) => (
    <svg
        width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ transform: direction === 'asc' ? 'rotate(180deg)' : 'none' }}
    >
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <polyline points="6 13 12 19 18 13"></polyline>
    </svg>
)

const getStatus = (item) => {
    if (item.deleteDate) {
        return { theme: 'gray', label: 'Deleted' }
    } else if (item.deadDate) {
        return { theme: 'red', label: 'Dead' }
    } else if (item.clicksDead > 0) {
        return { theme: 'amber', label: 'Reported Dead' }
    }

    return null
}

const getWasPrice = (item) => {
    if (item.priceOriginal && item.priceOriginal !== item.priceFinal) {
        return item.priceOriginal
    }

    return null
}

const getDiscountPercent = (item, wasPrice) => {
    if (!wasPrice)
        return null

    const original = parseFloat(wasPrice)
    const final = parseFloat(item.priceFinal)

    if (!original || Number.isNaN(final))
        return null

    const pct = Math.round((1 - (final / original)) * 100)

    return pct > 0 ? pct : null
}

const getCouponLabel = (item) => {
    if (item.couponPromoCode) {
        return `Code: ${item.couponPromoCode}`
    } else if (item.couponClickablePercentage) {
        return `${item.couponClickablePercentage}% off`
    } else if (item.couponClickableAmount) {
        return `$${item.couponClickableAmount} off`
    } else if (item.couponRedeemableDescription) {
        return item.couponRedeemableDescription
    }

    return null
}

const ListComponent = ({ value, config={}, events={} }) => {
    const [expandedIds, setExpandedIds] = useState(() => new Set())

    const handleSort = (column) => {
        if (events.handleSort)
            events.handleSort(column)
    }

    const handleEdit = (item) => {
        if (events.handleEdit)
            events.handleEdit(item)
    }

    const toggleExpanded = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev)

            if (next.has(id)) {
                next.delete(id)
            } else {
                next.add(id)
            }

            return next
        })
    }

    const renderSortLabel = (column, label) => (
        <TableSortLabel
            active={config.sortBy === column}
            direction={config.sortBy === column ? config.sortDir : 'desc'}
            onClick={() => handleSort(column)}
        >
            {label}
        </TableSortLabel>
    )

    return (
        <div className="c-List">
            <div className="list-header">
                <div className="list-headerFields">
                    <div className="list-headerField">{renderSortLabel('date', 'Date')}</div>
                    <div className="list-headerField">{renderSortLabel('clicksDead', 'Dead')}</div>
                    <div className="list-headerField">{renderSortLabel('name', 'Deal')}</div>
                    <div className="list-headerField">{renderSortLabel('price', 'Price')}</div>
                    <div className="list-headerField">{renderSortLabel('status', 'Status')}</div>
                    <div className="list-headerField">{renderSortLabel('clicks', 'Clicks')}</div>
                    
                </div>

                {/* not enough width for 6 sort labels on mobile — a dropdown
                    plus a direction toggle instead, same treatment as the
                    Tabs component. Selecting the already-active column here
                    re-triggers handleSort with the same column, which is
                    exactly what the direction toggle button needs too —
                    useControlSearch's setSort() flips direction when the
                    column passed in already matches the current sortBy */}
                <div className="list-headerSortMobile">
                    <FieldSelectComponent
                        value={config.sortBy}
                        config={{ label: 'Sort by', size: 'small', options: sortColumns }}
                        events={{ handleChange: handleSort }}
                    />

                    <button
                        type="button"
                        className="list-headerSortDirection"
                        onClick={() => handleSort(config.sortBy)}
                        aria-label={config.sortDir === 'asc' ? 'Sort ascending' : 'Sort descending'}
                    >
                        <SortDirectionIcon direction={config.sortDir} />
                    </button>
                </div>

                <div className="list-headerActions" />
            </div>

            <div className="list-rows">
                {value.map((item) => {
                    const status = getStatus(item)
                    const couponLabel = getCouponLabel(item)
                    const wasPrice = getWasPrice(item)
                    const discountPercent = getDiscountPercent(item, wasPrice)
                    const isExpanded = expandedIds.has(item.id)
                    const hasDetails = item.url || item.publishStartDate

                    return (
                        <div
                            className={`list-row ${status?.theme === 'red' ? 'list-row-dead' : ''} ${status?.theme === 'amber' ? 'list-row-reportedDead' : ''}`}
                            key={`list-row-${item.id}`}
                        >
                            <div className="list-rowHeader">
                                <div className="list-rowDate">{formatDate(item.date)}</div>

                                <div className="list-rowHeaderActions">
                                    <div className="list-tableActionsMenu">
                                        <button type="button" className="list-tableActionsTrigger" aria-label="Actions">
                                            Actions
                                        </button>
                                        <div className="list-tableActionsOverlay">
                                            {config.controls(item)}
                                        </div>
                                    </div>

                                    {item.url ? (
                                        <a
                                            className="list-rowExternalLink"
                                            href={item.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            aria-label="Open deal URL in new tab"
                                            title="Open deal URL"
                                        >
                                            <RedirectIcon />
                                        </a>
                                    ) : null}
                                </div>
                            </div>

                            <div className="list-rowTop">
                                <img
                                    className="list-rowThumb"
                                    src={item.imageUrl}
                                    alt={item.name}
                                    loading="lazy"
                                    onClick={() => handleEdit(item)}
                                />

                                <div className="list-rowMain">
                                    <div className="list-rowIdentity" onClick={() => handleEdit(item)}>
                                        <div className="list-rowAffiliate">{item.affiliate.name}</div>
                                        <div className="list-rowTitle">{item.name}</div>
                                    </div>

                                    {item.description ? <div className="list-rowDescription">{item.description}</div> : null}

                                    <div className="list-rowText">
                                        {(status || couponLabel) ? (
                                            <div className="list-rowBadges">
                                                {status ? <span className={`tag tag-${status.theme}`}>{status.label}</span> : null}
                                                {couponLabel ? <span className="list-couponBadge">{couponLabel}</span> : null}
                                            </div>
                                        ) : null}

                                        <div className="list-rowFields">
                                            <div className="list-field list-field-price">
                                                <span className="list-fieldLabel">Price</span>
                                                <span className="list-fieldValue">
                                                    <span className="now">${item.priceFinal}</span>
                                                    {wasPrice ? <span className="was">${wasPrice}</span> : null}
                                                </span>
                                            </div>

                                            <div className="list-field">
                                                <span className="list-fieldLabel">Discount</span>
                                                {discountPercent ? <span className="list-discountBadge">-{discountPercent}%</span> : <span className="list-fieldEmpty">—</span>}
                                            </div>

                                            <div className="list-field">
                                                <span className="list-fieldLabel">Clicks</span>
                                                <span className="list-fieldValue">{item.clicks}</span>
                                            </div>

                                            <div className="list-field">
                                                <span className="list-fieldLabel">Dead</span>
                                                <span className="list-fieldValue">{item.clicksDead}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {hasDetails ? (
                                <button
                                    type="button"
                                    className="list-rowExpandToggle"
                                    onClick={() => toggleExpanded(item.id)}
                                    aria-expanded={isExpanded}
                                >
                                    {isExpanded ? 'Less detail ▴' : 'More detail ▾'}
                                </button>
                            ) : null}

                            {isExpanded ? (
                                <div className="list-rowExpanded">
                                    {item.url ? (
                                        <div className="list-rowExpandedItem">
                                            <span className="list-fieldLabel">URL</span>
                                            <a href={item.url} target="_blank" rel="noreferrer" title={item.url}>{item.url}</a>
                                        </div>
                                    ) : null}

                                    {item.publishStartDate ? (
                                        <div className="list-rowExpandedItem">
                                            <div className="list-rowPublishWindow">
                                                <div className="list-field">
                                                    <span className="list-fieldLabel">Start</span>
                                                    <span className="list-fieldValue">{formatDate(item.publishStartDate)}</span>
                                                </div>
                                                <div className="list-field">
                                                    <span className="list-fieldLabel">End</span>
                                                    <span className="list-fieldValue">{item.publishEndDate ? formatDate(item.publishEndDate) : 'Ongoing'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export { ListComponent }
